import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SEO from "../../components/SEO";
import { api } from "../../lib/api";
import { hpaDownloadReport } from "../../lib/hpaApi";
import { HpaErrorState, HpaJourneyShell } from "./components";
import "./hpa.css";
import "./hpa-assessment.css";
import "./hpa-profile.css";

/**
 * SAT HPA™ · Block 11.3 · Profile Reveal
 *
 * Nur für RELEASED Profile (§ 11). Bei anderen States → Redirect nach /hpa.
 *
 * Sections:
 *   1. Hero
 *   2. Gesamtbild (Top Resources · Gaps · Tensions preview)
 *   3. Human Potential Signature (6 Achsen)
 *   4. Potential-vs-Expression (Gap Moment)
 *   5. Ressourcen (Cards)
 *   6. Spannungsfelder
 *   7. AURA Bridge (nur wenn gaps.length > 0)
 *   8. Dimension Details (Accordion)
 *   9. Report Download
 */

const AXIS_ORDER = ["SELF", "RELATIONAL", "ACTION", "ADAPTATION", "EXPRESSION", "MEANING"];

export default function HpaProfile() {
  const { profileId: paramId } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, profile: null, error: null });
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const loadProfile = useCallback(async () => {
    setState({ loading: true, profile: null, error: null });
    try {
      const path = paramId && paramId !== "current"
        ? `/hpa/profiles/${paramId}`
        : "/hpa/profiles/current";
      const p = await api.get(path).then((r) => r.data);
      if (p.lifecycle_state !== "RELEASED") {
        // § 11 · nur RELEASED
        navigate("/hpa", { replace: true });
        return;
      }
      setState({ loading: false, profile: p, error: null });
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) {
        navigate("/hpa", { replace: true });
        return;
      }
      setState({ loading: false, profile: null,
                 error: "Dein Profil konnte gerade nicht geladen werden." });
    }
  }, [paramId, navigate]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  const downloadReport = async () => {
    if (!state.profile || downloading) return;
    setDownloading(true);
    setDownloadError(null);
    try {
      await hpaDownloadReport(state.profile.profile_id);
    } catch (err) {
      const code = err?.response?.status;
      if (code === 404) {
        // Link vermutlich abgelaufen — einmal automatisch neu versuchen (§ 22)
        try {
          await hpaDownloadReport(state.profile.profile_id);
        } catch (_e2) {
          setDownloadError(
            "Der sichere Download-Link wird gerade neu erstellt. Bitte versuche es in wenigen Sekunden noch einmal."
          );
        }
      } else if (code === 503) {
        setDownloadError(
          "Der Report-Download ist gerade nicht aktiv. Bitte melde dich bei deiner:m SAT-Mentor:in."
        );
      } else {
        setDownloadError("Der Download konnte gerade nicht gestartet werden.");
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <SEO title="Mein HPA Profil" description="Human Potential Profile" path="/hpa/profile" />
      <HpaJourneyShell testid="hpa-profile">
        {state.loading && (
          <div
            className="text-[color:var(--hpa-text-mute)] mt-16 text-sm tracking-widest uppercase"
            data-testid="hpa-profile-loading"
          >
            Dein Profil wird geöffnet…
          </div>
        )}

        {state.error && (
          <div className="mt-8">
            <HpaErrorState message={state.error} onRetry={loadProfile} />
          </div>
        )}

        {state.profile && (
          <ProfileContent
            profile={state.profile}
            onDownload={downloadReport}
            downloading={downloading}
            downloadError={downloadError}
          />
        )}
      </HpaJourneyShell>
    </>
  );
}

// ============================================================================
// Content
// ============================================================================
function ProfileContent({ profile, onDownload, downloading, downloadError }) {
  return (
    <>
      {/* HERO ---------------------------------------------------------- */}
      <div className="mb-20" data-testid="hpa-profile-hero">
        <div className="hpa-overline mb-6">SAT HPA™ · MEIN PROFIL</div>
        <h1
          className="hpa-headline text-4xl md:text-6xl mb-8"
          data-testid="hpa-profile-hero-headline"
        >
          Dein Human <em>Potential Profile</em>.
        </h1>
        <p className="hpa-lede text-lg md:text-xl max-w-2xl leading-relaxed">
          Was in dir angelegt ist. Was davon heute sichtbar wird. Und wo deine
          nächste Entwicklungsfrage beginnen könnte.
        </p>
      </div>

      {/* GESAMTBILD ---------------------------------------------------- */}
      <Section title="Dein Gesamtbild" overline="Perspektive · Gesamtblick"
                testid="hpa-profile-gesamtbild">
        <ResourceGrid resources={profile.top_resources.slice(0, 3)} />
      </Section>

      {/* SIGNATURE ----------------------------------------------------- */}
      <Section
        title="Deine Human Potential Signature"
        overline="Sechs Achsen · Deine innere Landkarte"
        testid="hpa-profile-signature"
      >
        <HpaSignature axes={profile.axes} />
      </Section>

      {/* GAP MOMENT ---------------------------------------------------- */}
      {profile.gaps && profile.gaps.length > 0 && (
        <Section
          title="Wo mehr in dir steckt"
          overline="Potenzial trifft Alltag"
          testid="hpa-profile-gap-section"
        >
          <div className="hpa-gap-card" data-testid="hpa-profile-gap-card">
            <p className="hpa-lede text-lg md:text-xl leading-relaxed mb-8">
              Hier scheint mehr vorhanden zu sein, als aktuell sichtbar wird.
            </p>
            <p className="text-[15px] text-[color:var(--hpa-text-soft)] leading-relaxed mb-10 max-w-2xl">
              Deine HPA zeigt eine erkennbare Differenz zwischen vorhandener
              Ressource und ihrer aktuellen Expression.
            </p>
            <ul className="hpa-gap-list">
              {profile.gaps.slice(0, 3).map((g) => (
                <li
                  key={g.dimension_id}
                  className="hpa-gap-item"
                  data-testid={`hpa-gap-${g.dimension_id}`}
                >
                  <div>
                    <div className="hpa-gap-name">{g.dimension_label}</div>
                    <div className="hpa-gap-caption">
                      Potenzial: {bandLabelDE(g.potential_band)}. Alltag:{" "}
                      {bandLabelDE(g.expression_band)}.
                    </div>
                  </div>
                  <div className="hpa-overline" style={{ fontSize: "10px" }}>
                    {axisLabelDE(g.axis_id)}
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </Section>
      )}

      {/* RESOURCES ----------------------------------------------------- */}
      {profile.top_resources.length > 0 && (
        <Section
          title="Was dir besonders zur Verfügung steht"
          overline="Ressourcen · Stärken · Motivatoren"
          testid="hpa-profile-resources"
        >
          <ResourceGrid resources={profile.top_resources} />
        </Section>
      )}

      {/* TENSIONS ------------------------------------------------------ */}
      {profile.tensions && profile.tensions.length > 0 && (
        <Section
          title="Spannungsfelder, die einen zweiten Blick verdienen"
          overline="Nuancen · Widersprüche"
          testid="hpa-profile-tensions"
        >
          <p className="hpa-lede text-base leading-relaxed max-w-2xl mb-8">
            Widersprüche sind nicht automatisch Probleme. Oft zeigen sie, dass
            eine Ressource je nach Situation unterschiedlich verfügbar ist.
          </p>
          {profile.tensions.map((t) => (
            <div
              key={t.tension_id}
              className="hpa-tension-card"
              data-testid={`hpa-tension-${t.tension_id}`}
            >
              <div className="hpa-overline" style={{ fontSize: "10px", marginBottom: "8px" }}>
                {tensionKindLabelDE(t.tension_kind)}
              </div>
              <div className="text-[15px] text-[color:var(--hpa-text-soft)]">
                {t.dimension_id ? dimensionCaption(t.dimension_id) :
                  "Ein Spannungsfeld, das durch mehrere Ebenen deiner HPA sichtbar wird."}
              </div>
            </div>
          ))}
        </Section>
      )}

      {/* AURA BRIDGE --------------------------------------------------- */}
      {profile.aura_bridge && (
        <Section testid="hpa-profile-aura-bridge">
          <div className="hpa-aura-bridge">
            <div className="hpa-aura-label">AURA · Decode the Pattern</div>
            <h2 className="hpa-headline text-2xl md:text-3xl mb-6 max-w-2xl">
              Warum wird manches von dem, was in dir steckt, nicht überall <em>sichtbar</em>?
            </h2>
            <p className="hpa-lede text-base md:text-lg leading-relaxed max-w-2xl">
              Genau dort beginnt die nächste Ebene: nicht mehr nur was in dir
              steckt, sondern welches wiederkehrende Muster zwischen Potenzial
              und gelebter Realität wirken könnte.
            </p>
            <p className="mt-8 text-xs uppercase tracking-[0.28em] text-[color:var(--hpa-text-mute)]">
              AURA wird in Kürze innerhalb der SAT-Akademie freigeschaltet.
            </p>
          </div>
        </Section>
      )}

      {/* DIMENSION DETAILS -------------------------------------------- */}
      <Section
        title="Alle Dimensionen ansehen"
        overline="Details · Zum Nachlesen"
        testid="hpa-profile-dimensions"
      >
        <div className="hpa-dim-accordion">
          {profile.dimensions.map((d) => (
            <details
              key={d.dimension_id}
              className="hpa-dim-row"
              data-testid={`hpa-dim-row-${d.dimension_id}`}
            >
              <summary>
                <span className="hpa-dim-row-title">{d.dimension_label}</span>
                <span className="hpa-dim-row-band">{bandLabelDE(d.display_band)}</span>
              </summary>
              <div className="hpa-dim-detail-grid">
                <div>
                  <div className="hpa-overline" style={{ fontSize: "9px" }}>Potenzial</div>
                  <div className="text-[color:var(--hpa-text)]">
                    {bandLabelDE(d.potential_band || d.display_band)}
                  </div>
                </div>
                <div>
                  <div className="hpa-overline" style={{ fontSize: "9px" }}>Alltag</div>
                  <div className="text-[color:var(--hpa-text)]">
                    {bandLabelDE(d.expression_band || d.display_band)}
                  </div>
                </div>
                <div>
                  <div className="hpa-overline" style={{ fontSize: "9px" }}>Achse</div>
                  <div className="text-[color:var(--hpa-text)]">
                    {axisLabelDE(d.axis_id)}
                  </div>
                </div>
                <div>
                  <div className="hpa-overline" style={{ fontSize: "9px" }}>Konfidenz</div>
                  <div className="text-[color:var(--hpa-text)]">
                    {d.confidence >= 0.75 ? "Hoch"
                     : d.confidence >= 0.5 ? "Mittel"
                     : "Vorläufig"}
                  </div>
                </div>
              </div>
            </details>
          ))}
        </div>
      </Section>

      {/* REPORT DOWNLOAD ---------------------------------------------- */}
      <Section testid="hpa-profile-report">
        <div className="hpa-card">
          <div className="hpa-overline mb-3">Dein HPA Report</div>
          <h2 className="hpa-headline text-2xl md:text-3xl mb-6">
            Dein persönlicher HPA-Report als <em>PDF</em>.
          </h2>
          <p className="hpa-lede text-base leading-relaxed max-w-2xl mb-8">
            Ein PDF-Dokument mit deiner vollständigen HPA-Auswertung — für dich
            selbst und für den Austausch mit deiner:m SAT-Mentor:in.
          </p>
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <button
              type="button"
              className="hpa-btn-primary"
              disabled={downloading}
              onClick={onDownload}
              data-testid="hpa-profile-cta-download"
            >
              {downloading ? "Sicherer Download wird erstellt…"
                            : "Meinen HPA Report herunterladen"}
            </button>
            <Link to="/hpa" className="hpa-btn-ghost" data-testid="hpa-profile-cta-home">
              Zur HPA-Übersicht
            </Link>
          </div>
          {downloadError && (
            <div className="mt-6 text-sm text-[color:#E8A6A6]"
                  data-testid="hpa-profile-download-error">
              {downloadError}
            </div>
          )}
        </div>
      </Section>
    </>
  );
}

// ============================================================================
// Helpers
// ============================================================================
function Section({ title, overline, children, testid }) {
  return (
    <section className="mb-24" data-testid={testid}>
      {overline && <div className="hpa-overline mb-4">{overline}</div>}
      {title && (
        <h2 className="hpa-headline text-2xl md:text-4xl mb-10">{title}</h2>
      )}
      {children}
    </section>
  );
}

function HpaSignature({ axes }) {
  const byId = Object.fromEntries((axes || []).map((a) => [a.axis_id, a]));
  return (
    <>
      {/* Screen-Reader-Zusammenfassung (§ 27) */}
      <div className="hpa-sr-only" data-testid="hpa-signature-sr">
        {(axes || []).map((a) => (
          <span key={a.axis_id}>
            {a.axis_label_de}: Potenzial {bandLabelDE(a.potential_band)},
            Alltag {bandLabelDE(a.expression_band)}.
          </span>
        ))}
      </div>
      <div className="hpa-signature-wrapper" role="list">
        {AXIS_ORDER.map((axisId) => {
          const a = byId[axisId];
          if (!a) return null;
          return (
            <div
              key={axisId}
              className="hpa-axis-card"
              role="listitem"
              data-testid={`hpa-axis-${axisId}`}
            >
              <div className="hpa-axis-label">{axisLabelDE(axisId)}</div>
              <div className="hpa-axis-name">{a.axis_label_de}</div>
              <div className="hpa-two-level">
                <div className="hpa-level-row" data-kind="potential">
                  <span className="hpa-level-label">Potenzial</span>
                  <span className="hpa-level-bar">
                    <span className="hpa-level-bar-fill"
                           data-band={a.potential_band} />
                  </span>
                </div>
                <div className="hpa-level-row" data-kind="expression">
                  <span className="hpa-level-label">Alltag</span>
                  <span className="hpa-level-bar">
                    <span className="hpa-level-bar-fill"
                           data-band={a.expression_band} />
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function ResourceGrid({ resources }) {
  return (
    <div className="hpa-resource-grid">
      {resources.map((r, i) => (
        <div
          key={`${r.kind}-${r.label}-${i}`}
          className="hpa-resource-card"
          data-testid={`hpa-resource-${i}`}
        >
          <span className="hpa-resource-kind">{resourceKindLabelDE(r.kind)}</span>
          <div className="hpa-resource-label">{r.label}</div>
        </div>
      ))}
    </div>
  );
}

// ============================================================================
// Copy-Mapping (§ 13, § 21)
// ============================================================================
function bandLabelDE(band) {
  return {
    HIGH: "Deutlich vorhanden",
    PRONOUNCED: "Klar vorhanden",
    MODERATE: "Angelegt",
    LOW: "Bisher wenig sichtbar",
    NOT_YET_MEASURED: "Noch nicht gemessen",
    MEDIUM: "Angelegt",
    INSUFFICIENT_EVIDENCE: "Vorläufig",
  }[band] || band;
}
function axisLabelDE(axisId) {
  return {
    SELF: "Selbst",
    RELATIONAL: "Beziehung",
    ACTION: "Handlung",
    ADAPTATION: "Anpassung",
    EXPRESSION: "Ausdruck",
    MEANING: "Sinn",
  }[axisId] || axisId || "—";
}
function resourceKindLabelDE(kind) {
  return {
    STRENGTH: "Ausgeprägte Stärke",
    RESOURCE: "Ressource",
    MOTIVATOR: "Motivator",
    STRESS: "Stresstendenz",
  }[kind] || kind;
}
function tensionKindLabelDE(kind) {
  return {
    CROSS_SOURCE: "Zwischen unterschiedlichen Perspektiven",
    CONTEXTUAL: "Situationsabhängig",
    POTENTIAL_EXPRESSION_GAP: "Zwischen Potenzial und Alltag",
    CONFLICTING_STRENGTHS: "Zwischen mehreren Ressourcen",
  }[kind] || "Ein Spannungsfeld";
}
function dimensionCaption(dimensionId) {
  return `Dieses Muster wird über ${dimensionId} sichtbar — je nach Situation unterschiedlich stark.`;
}
